/* "How do you use AI at work?" · internal survey, FR / NL / EN.
 *
 * Answers are stored by question id and option id, never by label, so
 * tallies line up whatever language people answered in and a label can be
 * reworded later without breaking the results.
 *
 * Question shape:
 *   { id, type: 'single' | 'multi' | 'scale' | 'text' | 'matrix', q, hint?, opts?,
 *     other?, max?, showIf?: answers => bool }
 * A `matrix` has `rows` and `levels` (same [[id, label]] shape) and saves
 * `{row_id: level_id}`.
 * `opts` is [[id, {fr, nl, en}], ...]. `other: true` adds a free-text
 * "Other…" choice saved under `<id>_other`. A question hidden by `showIf`
 * is dropped from the saved answers.
 *
 * Department isn't asked · it comes from the directory and is snapshotted
 * with the answers by the backend.
 */

const L = (fr, nl, en) => ({ fr, nl, en });

// Usage questions show by default and only drop away once someone says "Never".
const usesAI = (a) => a.freq !== 'never';

export const AI_SURVEY = {
  slug: 'ai-at-work',
  intro: {
    eyebrow: L('Enquête interne · ±8 minutes', 'Interne bevraging · ±8 minuten', 'Internal survey · ±8 minutes'),
    title: L('Comment utilisez-vous l’IA au travail ?', 'Hoe gebruik jij AI op het werk?', 'How do you use AI at work?'),
    lede: L(
      'Nous voulons savoir qui utilise déjà des outils d’IA, pour quoi faire, et où ils pourraient aider davantage. Il n’y a pas de mauvaise réponse, et « je ne l’utilise pas » nous intéresse aussi. Vous pouvez revenir modifier vos réponses plus tard.',
      'We willen een duidelijk beeld van wie al AI-tools gebruikt, waarvoor, en waar het nog meer kan helpen. Er zijn geen foute antwoorden, en "ik gebruik het niet" is ook nuttig om te weten. Je kan later terugkomen om je antwoorden aan te passen.',
      'We want a clear picture of who already uses AI tools, what for, and where it could help more. There are no wrong answers, and "I don’t use it" is useful to know too. You can come back and change your answers later.',
    ),
    privacy: L(
      'Vos réponses sont visibles par les RH et l’équipe qui pilote le projet IA, avec votre nom, pour pouvoir organiser des formations et vous recontacter. Elles ne sont pas partagées au-delà.',
      'Je antwoorden zijn zichtbaar voor HR en het team achter het AI-project, met je naam, zodat we opleidingen kunnen plannen en je kunnen contacteren. Verder worden ze niet gedeeld.',
      'Your answers are visible to HR and the team running the AI project, with your name, so we can plan training and follow up with you. They aren’t shared any further.',
    ),
  },
  sections: [
    {
      title: L('Aujourd’hui', 'Vandaag', 'Today'),
      questions: [
        {
          id: 'freq', type: 'single', required: true,
          q: L('À quelle fréquence utilisez-vous des outils d’IA pour votre travail ?', 'Hoe vaak gebruik je AI-tools voor je werk?', 'How often do you use AI tools for your work?'),
          opts: [
            ['daily',   L('Tous les jours', 'Dagelijks', 'Daily')],
            ['weekly',  L('Quelques fois par semaine', 'Een paar keer per week', 'A few times a week')],
            ['monthly', L('Quelques fois par mois', 'Een paar keer per maand', 'A few times a month')],
            ['tried',   L('Essayé une ou deux fois', 'Eén of twee keer geprobeerd', 'Tried it once or twice')],
            ['never',   L('Jamais', 'Nooit', 'Never')],
          ],
        },
        {
          id: 'feeling', type: 'single',
          q: L('Comment vous sentez-vous face à l’IA dans votre travail ?', 'Hoe sta je tegenover AI in je werk?', 'How do you feel about AI in your work?'),
          hint: L('Votre ressenti honnête nous aide à bien accompagner le changement.', 'Je eerlijke gevoel helpt ons om de verandering goed te begeleiden.', 'Your honest take helps us handle the change well.'),
          opts: [
            ['enthusiastic', L('Enthousiaste', 'Enthousiast', 'Enthusiastic')],
            ['curious',      L('Curieux·se', 'Nieuwsgierig', 'Curious')],
            ['neutral',      L('Neutre', 'Neutraal', 'Neutral')],
            ['sceptical',    L('Sceptique', 'Sceptisch', 'Sceptical')],
            ['worried',      L('Inquiet·ète', 'Bezorgd', 'Worried')],
          ],
        },
        {
          id: 'private', type: 'single',
          q: L('Utilisez-vous aussi l’IA en dehors du travail ?', 'Gebruik je AI ook buiten het werk?', 'Do you also use AI outside work?'),
          hint: L('Pour vos vacances, vos finances, le sport, l’école des enfants…', 'Voor je reizen, financiën, sport, het huiswerk van de kinderen…', 'For trips, money matters, sport, the kids’ homework…'),
          opts: [
            ['often',     L('Souvent', 'Vaak', 'Often')],
            ['sometimes', L('Parfois', 'Soms', 'Sometimes')],
            ['never',     L('Jamais', 'Nooit', 'Never')],
          ],
        },
        {
          id: 'know', type: 'matrix',
          q: L('Que connaissez-vous de ce que l’IA sait faire ?', 'Wat ken je van wat AI vandaag kan?', 'How familiar are you with what AI can do today?'),
          hint: L('Une ligne par possibilité. Ce n’est pas un test : « jamais entendu parler » est une réponse tout à fait normale.', 'Eén lijn per mogelijkheid. Dit is geen test: "nooit van gehoord" is een heel normaal antwoord.', 'One line per capability. This isn’t a test: "never heard of it" is a perfectly normal answer.'),
          levels: [
            ['unknown', L('Jamais entendu parler', 'Nooit van gehoord', 'Never heard of it')],
            ['heard',   L('Déjà entendu parler', 'Van gehoord', 'Heard of it')],
            ['tried',   L('Déjà essayé', 'Al geprobeerd', 'Tried it')],
            ['regular', L('J’utilise régulièrement', 'Gebruik ik geregeld', 'Use it regularly')],
          ],
          rows: [
            ['files',     L('Lui donner un PDF, un Excel ou une photo et poser des questions dessus', 'Een pdf, Excel of foto geven en er vragen over stellen', 'Giving it a PDF, Excel file or photo and asking questions about it')],
            ['web',       L('Lui faire chercher sur internet, avec ses sources', 'Het laten zoeken op het internet, met bronnen', 'Having it search the web and cite its sources')],
            ['context',   L('Enregistrer des instructions ou documents pour qu’elle connaisse votre contexte (Projets, GPTs)', 'Instructies of documenten bewaren zodat het je context kent (Projects, GPTs)', 'Saving instructions or documents so it knows your context (Projects, GPTs)')],
            ['connected', L('La connecter à votre mail, agenda ou Drive', 'Het koppelen aan je mail, agenda of Drive', 'Connecting it to your email, calendar or Drive')],
          ],
        },
        {
          id: 'tools', type: 'multi', other: true, showIf: usesAI,
          q: L('Quels outils utilisez-vous ?', 'Welke tools gebruik je?', 'Which tools do you use?'),
          hint: L('Plusieurs réponses possibles.', 'Meerdere antwoorden mogelijk.', 'Select all that apply.'),
          opts: [
            ['chatgpt',    L('ChatGPT', 'ChatGPT', 'ChatGPT')],
            ['claude',     L('Claude', 'Claude', 'Claude')],
            ['claude_code',L('Claude Code / Cody', 'Claude Code / Cody', 'Claude Code / Cody')],
            ['gemini',     L('Google Gemini', 'Google Gemini', 'Google Gemini')],
            ['copilot',    L('Microsoft Copilot', 'Microsoft Copilot', 'Microsoft Copilot')],
            ['perplexity', L('Perplexity', 'Perplexity', 'Perplexity')],
            ['deepl',      L('DeepL', 'DeepL', 'DeepL')],
            ['images',     L('Outils d’images (Midjourney, Firefly…)', 'Beeldtools (Midjourney, Firefly…)', 'Image tools (Midjourney, Firefly…)')],
            ['builtin',    L('IA intégrée à d’autres logiciels (Shopify, Canva…)', 'AI ingebouwd in andere software (Shopify, Canva…)', 'AI built into other software (Shopify, Canva…)')],
          ],
        },
        {
          id: 'account', type: 'single', showIf: usesAI,
          q: L('Quel type de compte utilisez-vous ?', 'Wat voor account gebruik je?', 'What kind of account do you use?'),
          opts: [
            ['free',    L('Compte gratuit', 'Gratis account', 'Free account')],
            ['self',    L('Payé moi-même', 'Zelf betaald', 'Paid myself')],
            ['company', L('Payé par l’entreprise', 'Betaald door het bedrijf', 'Paid by the company')],
            ['mix',     L('Un mélange', 'Een mix', 'A mix')],
            ['unsure',  L('Je ne sais pas', 'Weet ik niet', 'Not sure')],
          ],
        },
        {
          id: 'uses', type: 'multi', other: true, showIf: usesAI,
          q: L('Pour quoi l’utilisez-vous déjà ?', 'Waarvoor gebruik je het al?', 'What do you already use it for?'),
          hint: L('Plusieurs réponses possibles.', 'Meerdere antwoorden mogelijk.', 'Select all that apply.'),
          opts: [
            ['customer_emails', L('Rédiger ou répondre aux e-mails clients', 'Klantenmails schrijven of beantwoorden', 'Writing or answering customer emails')],
            ['translate',       L('Traduire (NL / FR / EN / DE)', 'Vertalen (NL / FR / EN / DE)', 'Translating (NL / FR / EN / DE)')],
            ['product_copy',    L('Descriptions produits & textes SEO', 'Productbeschrijvingen & SEO-teksten', 'Product descriptions & SEO texts')],
            ['supplier_docs',   L('Résumer des documents ou catalogues fournisseurs', 'Leveranciersdocumenten of catalogi samenvatten', 'Summarising supplier documents or catalogues')],
            ['excel',           L('Excel, formules & nettoyage de données', 'Excel, formules & data opkuisen', 'Excel, formulas & data cleaning')],
            ['social',          L('Réseaux sociaux & newsletters', 'Sociale media & nieuwsbrieven', 'Social media & newsletters')],
            ['visuals',         L('Images & visuels produits', 'Beelden & productvisuals', 'Images & product visuals')],
            ['research',        L('Recherche & comparaison de produits', 'Opzoekwerk & producten vergelijken', 'Research & comparing products')],
            ['investigate',     L('Comprendre pourquoi quelque chose cloche (commande, facture, prix…)', 'Uitzoeken waarom iets misloopt (bestelling, factuur, prijs…)', 'Figuring out why something went wrong (an order, invoice, price…)')],
            ['reports',         L('Rapports, analyses & présentations pour des collègues', 'Rapporten, analyses & presentaties voor collega’s', 'Reports, analyses & presentations for colleagues')],
            ['code',            L('Créer de petits outils, apps ou automatisations', 'Kleine tools, apps of automatisaties bouwen', 'Building small tools, apps or automations')],
            ['brainstorm',      L('Brainstorming & planification', 'Brainstormen & plannen', 'Brainstorming & planning')],
          ],
        },
        {
          id: 'depth', type: 'single', showIf: usesAI,
          q: L('Jusqu’où laissez-vous aller l’IA ?', 'Hoe ver laat je AI gaan?', 'How far do you let AI go?'),
          hint: L('Choisissez le niveau le plus avancé que vous utilisez, même de temps en temps.', 'Kies het verste niveau dat je gebruikt, ook al is het maar af en toe.', 'Pick the furthest level you use, even if only now and then.'),
          opts: [
            ['ask',     L('Je pose des questions, je demande conseil', 'Ik stel vragen, vraag advies', 'I ask questions and get advice')],
            ['draft',   L('Elle rédige, je copie-colle et j’ajuste', 'Het schrijft iets, ik kopieer en pas aan', 'It drafts, I copy-paste and adjust')],
            ['files',   L('Elle travaille sur mes fichiers (Excel, PDF, captures d’écran)', 'Het werkt met mijn bestanden (Excel, pdf, screenshots)', 'It works on my files (Excel, PDF, screenshots)')],
            ['acts',    L('Elle agit dans nos outils (Slack, Odoo, mail, webshop…)', 'Het voert acties uit in onze tools (Slack, Odoo, mail, webshop…)', 'It takes actions in our tools (Slack, Odoo, mail, webshop…)')],
            ['builds',  L('Elle construit des outils ou automatisations pour moi', 'Het bouwt tools of automatisaties voor mij', 'It builds tools or automations for me')],
            ['runs',    L('Elle tourne seule en arrière-plan (rapports récurrents, alertes…)', 'Het draait zelfstandig op de achtergrond (terugkerende rapporten, alerts…)', 'It runs on its own in the background (recurring reports, alerts…)')],
          ],
        },
        {
          id: 'best', type: 'text', showIf: usesAI,
          q: L('Décrivez votre usage de l’IA le plus utile.', 'Beschrijf je nuttigste AI-toepassing.', 'Describe your most useful AI use case.'),
          hint: L('Que faites-vous, avec quel outil, et que vous fait-il gagner ? Un exemple concret aide beaucoup.', 'Wat doe je, met welke tool, en wat levert het je op? Een concreet voorbeeld helpt enorm.', 'What do you do, which tool, and what does it save you? A concrete example helps a lot.'),
        },
        {
          id: 'time', type: 'single', showIf: usesAI,
          q: L('Combien de temps l’IA vous fait-elle gagner par semaine, environ ?', 'Hoeveel tijd bespaart AI je ongeveer per week?', 'Roughly how much time does AI save you per week?'),
          opts: [
            ['none', L('Aucun', 'Geen', 'None')],
            ['lt1',  L('Moins d’1 heure', 'Minder dan 1 uur', 'Less than 1 hour')],
            ['1_3',  L('1 à 3 heures', '1 tot 3 uur', '1–3 hours')],
            ['3_5',  L('3 à 5 heures', '3 tot 5 uur', '3–5 hours')],
            ['gt5',  L('Plus de 5 heures', 'Meer dan 5 uur', 'More than 5 hours')],
          ],
        },
        {
          id: 'trust', type: 'single', showIf: usesAI,
          q: L('À quelle fréquence devez-vous corriger ce que l’IA vous donne ?', 'Hoe vaak moet je verbeteren wat AI je geeft?', 'How often do you need to correct what the AI gives you?'),
          opts: [
            ['rarely',    L('Rarement, c’est souvent bon du premier coup', 'Zelden, het is meestal meteen goed', 'Rarely, it’s usually right first time')],
            ['sometimes', L('Parfois, quelques retouches', 'Soms, een paar aanpassingen', 'Sometimes, a few tweaks')],
            ['often',     L('Souvent, je retravaille beaucoup', 'Vaak, ik herwerk veel', 'Often, I rework a lot')],
            ['always',    L('Je vérifie tout systématiquement', 'Ik controleer alles systematisch', 'I check everything, every time')],
          ],
        },
        {
          id: 'data', type: 'multi', showIf: usesAI,
          q: L('Quel type d’informations collez-vous dans les outils d’IA ?', 'Wat voor informatie plak je in AI-tools?', 'What kind of information do you paste into AI tools?'),
          hint: L('Des réponses honnêtes nous aident à fixer des règles raisonnables. Personne n’aura d’ennuis.', 'Eerlijke antwoorden helpen ons om redelijke afspraken te maken. Niemand komt hierdoor in de problemen.', 'Honest answers help us set sensible guidelines. Nobody gets in trouble for this.'),
          opts: [
            ['nothing',  L('Rien de sensible', 'Niets gevoeligs', 'Nothing sensitive')],
            ['customer', L('Noms, adresses ou commandes de clients', 'Namen, adressen of bestellingen van klanten', 'Customer names, addresses or orders')],
            ['supplier', L('Prix ou conditions fournisseurs', 'Prijzen of voorwaarden van leveranciers', 'Supplier prices or conditions')],
            ['figures',  L('Chiffres internes (ventes, marges)', 'Interne cijfers (omzet, marges)', 'Internal figures (sales, margins)')],
            ['catalogue',L('Données produits de notre catalogue', 'Productdata uit onze catalogus', 'Product data from our catalogue')],
            ['unsure',   L('Je ne sais pas', 'Weet ik niet', 'Not sure')],
          ],
        },
      ],
    },
    {
      title: L('Votre travail', 'Jouw werk', 'Your work'),
      questions: [
        {
          id: 'timesinks', type: 'text',
          q: L('Qu’est-ce qui vous prend le plus de temps dans une semaine ?', 'Wat neemt de meeste tijd in je week in?', 'What takes up most of your time in a week?'),
          hint: L('Décrivez votre semaine type : quelles tâches prennent le plus de temps, et environ combien ? Que l’IA puisse aider ou non, on veut savoir où part le temps.', 'Beschrijf je typische week: welke taken nemen de meeste tijd in, en ongeveer hoeveel? Of AI kan helpen of niet: we willen weten waar de tijd naartoe gaat.', 'Describe a typical week: which tasks take the most time, and roughly how much? Whether or not AI could help, we want to know where the time goes.'),
        },
        {
          id: 'wish', type: 'text',
          q: L('Pour quelle tâche aimeriez-vous que l’IA vous aide ?', 'Bij welke taak zou je graag hulp van AI krijgen?', 'Which task would you love AI to help with?'),
          hint: L('Pensez au travail répétitif, lent ou ennuyeux. Dites-nous aussi à quelle fréquence il revient et combien de temps il prend.', 'Denk aan repetitief, traag of saai werk. Vertel er ook bij hoe vaak het terugkomt en hoe lang het duurt.', 'Think of repetitive, slow or boring work. Tell us too how often it comes up and how long it takes.'),
        },
        {
          id: 'dream', type: 'text',
          q: L('Votre rêve : une IA qui connaît LampTwist par cœur. Que ferait-elle pour vous ?', 'Droomscenario: een AI die LampTwist door en door kent. Wat zou die voor jou doen?', 'Dream scenario: an AI that knows LampTwist inside out. What would it do for you?'),
          hint: L('Voyez grand, sans limite technique. Décrivez un moment de votre semaine où elle ferait toute la différence.', 'Denk groot, zonder technische grenzen. Beschrijf een moment in je week waar die het verschil zou maken.', 'Think big, no technical limits. Describe a moment in your week where it would make all the difference.'),
        },
        {
          id: 'connect', type: 'multi', other: true,
          q: L('Avec quels outils de LampTwist aimeriez-vous que l’IA puisse travailler directement ?', 'Met welke LampTwist-tools zou AI rechtstreeks moeten kunnen werken?', 'Which of our tools would you most like AI to work with directly?'),
          hint: L('Par exemple : retrouver une commande dans Odoo ou préparer une réponse depuis votre boîte mail.', 'Bijvoorbeeld: een bestelling opzoeken in Odoo of een antwoord klaarzetten vanuit je mailbox.', 'For example: looking up an order in Odoo, or drafting a reply from your mailbox.'),
          opts: [
            ['apps',      L('Toutes nos apps internes (Sangly, Shippy, Purchy, PIM…)', 'Al onze interne apps (Sangly, Shippy, Purchy, PIM…)', 'All our internal apps (Sangly, Shippy, Purchy, PIM…)')],
            ['odoo',      L('Odoo', 'Odoo', 'Odoo')],
            ['gmail',     L('Gmail / ma boîte mail', 'Gmail / mijn mailbox', 'Gmail / my mailbox')],
            ['shopify',   L('Shopify / le webshop', 'Shopify / de webshop', 'Shopify / the webshop')],
            ['slack',     L('Slack', 'Slack', 'Slack')],
            ['drive',     L('Google Drive & Sheets', 'Google Drive & Sheets', 'Google Drive & Sheets')],
            ['notion',    L('Notion', 'Notion', 'Notion')],
            ['catalogues',L('Catalogues & listes de prix fournisseurs', 'Catalogi & prijslijsten van leveranciers', 'Supplier catalogues & price lists')],
          ],
        },
        {
          id: 'nogo', type: 'text',
          q: L('Y a-t-il des choses que vous préféreriez que l’IA ne fasse pas dans votre travail ?', 'Zijn er dingen die AI liever niet zou doen in jouw werk?', 'Is there anything you’d rather AI did not do in your work?'),
          hint: L('Là où le contact humain compte, ou là où vous ne lui feriez pas confiance.', 'Waar het menselijke contact telt, of waar je het niet zou vertrouwen.', 'Where the human touch matters, or where you wouldn’t trust it.'),
        },
      ],
    },
    {
      title: L('Pour y arriver', 'Hoe komen we er', 'Getting there'),
      questions: [
        {
          id: 'blockers', type: 'multi', other: true,
          q: L('Qu’est-ce qui vous freine pour utiliser davantage l’IA ?', 'Wat houdt je tegen om AI meer te gebruiken?', 'What holds you back from using AI more?'),
          opts: [
            ['start',   L('Je ne sais pas par où commencer', 'Ik weet niet waar te beginnen', 'Don’t know where to start')],
            ['correct', L('Pas sûr·e que le résultat soit correct', 'Niet zeker of het resultaat klopt', 'Not sure the output is correct')],
            ['privacy', L('Inquiet·ète pour la vie privée / les données clients', 'Bezorgd om privacy / klantgegevens', 'Worried about privacy / customer data')],
            ['account', L('Pas de compte (payant)', 'Geen (betaald) account', 'No (paid) account')],
            ['time',    L('Pas le temps d’apprendre', 'Geen tijd om het te leren', 'No time to learn it')],
            ['allowed', L('Pas sûr·e que ce soit autorisé', 'Niet zeker of het mag', 'Not sure it’s allowed')],
            ['nothing', L('Rien, je l’utilise beaucoup', 'Niets, ik gebruik het volop', 'Nothing, I use it plenty')],
          ],
        },
        {
          id: 'training', type: 'single',
          q: L('Participeriez-vous à une courte session pratique sur l’IA au travail ?', 'Zou je deelnemen aan een korte praktische sessie over AI op het werk?', 'Would you join a short hands-on session on using AI at work?'),
          opts: [
            ['yes',   L('Oui, volontiers', 'Ja, graag', 'Yes, please')],
            ['maybe', L('Peut-être', 'Misschien', 'Maybe')],
            ['no',    L('Non merci', 'Nee, bedankt', 'No thanks')],
          ],
        },
        {
          id: 'format', type: 'multi',
          showIf: (a) => a.training === 'yes' || a.training === 'maybe',
          q: L('Quel format vous conviendrait le mieux ?', 'Welk formaat past jou het best?', 'Which format would suit you best?'),
          hint: L('Plusieurs réponses possibles.', 'Meerdere antwoorden mogelijk.', 'Select all that apply.'),
          opts: [
            ['workshop', L('Atelier pratique en groupe', 'Praktische workshop in groep', 'Hands-on group workshop')],
            ['team',     L('Session avec ma propre équipe, sur nos tâches', 'Sessie met mijn eigen team, op onze taken', 'Session with my own team, on our tasks')],
            ['video',    L('Courtes vidéos à regarder quand je veux', 'Korte video’s om te kijken wanneer ik wil', 'Short videos to watch when I like')],
            ['guide',    L('Un guide écrit / aide-mémoire', 'Een geschreven gids / spiekbrief', 'A written guide / cheat sheet')],
            ['buddy',    L('Un collègue qui me montre, en tête-à-tête', 'Een collega die het me één-op-één toont', 'A colleague showing me one-to-one')],
            ['slack',    L('Des astuces régulières sur Slack', 'Regelmatige tips op Slack', 'Regular tips on Slack')],
          ],
        },
        {
          id: 'demo', type: 'single',
          q: L('Montreriez-vous votre propre usage à vos collègues (5–10 min) ?', 'Zou je je eigen toepassing aan collega’s tonen (5–10 min)?', 'Would you show your own use case to colleagues (5–10 min)?'),
          opts: [
            ['yes',   L('Oui', 'Ja', 'Yes')],
            ['maybe', L('Peut-être', 'Misschien', 'Maybe')],
            ['no',    L('Non', 'Nee', 'No')],
          ],
        },
        {
          id: 'remarks', type: 'text',
          q: L('Autre chose à partager ?', 'Nog iets dat je wil delen?', 'Anything else you’d like to share?'),
        },
      ],
    },
  ],
};

export const allQuestions = (survey) => survey.sections.flatMap(s => s.questions);

export const isVisible = (q, answers) => !q.showIf || q.showIf(answers);

/* Answers as they should be saved · hidden questions and empty values out. */
export function cleanAnswers(survey, answers) {
  const out = {};
  for (const q of allQuestions(survey)) {
    if (!isVisible(q, answers)) continue;
    const raw = answers[q.id];
    const pickedOther = Array.isArray(raw) ? raw.includes('__other') : raw === '__other';
    // "Other…" is saved as its text alone, under `<id>_other`.
    const v = Array.isArray(raw) ? raw.filter(x => x !== '__other') : raw === '__other' ? '' : raw;
    const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0)
      || (q.type === 'matrix' && Object.keys(v || {}).length === 0);
    if (!empty) out[q.id] = typeof v === 'string' ? v.trim() : v;
    const other = (answers[`${q.id}_other`] || '').trim();
    if (q.other && pickedOther && other) out[`${q.id}_other`] = other;
  }
  return out;
}

/* Label for an option id in the viewer's locale · falls back to the id. */
export function optionLabel(q, id, locale) {
  if (id === '__other') return UI.other[locale] || UI.other.fr;
  if (q.type === 'scale') return String(id);
  if (q.type === 'matrix') {
    const lvl = (q.levels || []).find(([lid]) => lid === id);
    return lvl ? (lvl[1][locale] || lvl[1].fr) : id;
  }
  const hit = (q.opts || []).find(([oid]) => oid === id);
  return hit ? (hit[1][locale] || hit[1].fr) : id;
}

export const UI = {
  answeringAs:  L('Vous répondez en tant que', 'Je antwoordt als', 'Answering as'),
  other:        L('Autre…', 'Andere…', 'Other…'),
  otherPh:      L('Précisez', 'Specifieer', 'Please specify'),
  submit:       L('Envoyer mes réponses', 'Antwoorden versturen', 'Submit answers'),
  update:       L('Mettre à jour mes réponses', 'Antwoorden bijwerken', 'Update answers'),
  saving:       L('Enregistrement…', 'Opslaan…', 'Saving…'),
  saved:        L('Merci ! Vos réponses sont enregistrées. Vous pouvez les modifier à tout moment.', 'Bedankt! Je antwoorden zijn opgeslagen. Je kan ze altijd aanpassen.', 'Thanks! Your answers are saved. You can change them any time.'),
  loaded:       L('Vos réponses précédentes sont chargées.', 'Je eerdere antwoorden zijn geladen.', 'Your earlier answers are loaded.'),
  needFreq:     L('Répondez d’abord à la question 1.', 'Beantwoord eerst vraag 1.', 'Please answer question 1 first.'),
  failed:       L('L’enregistrement a échoué. Vérifiez votre connexion et réessayez.', 'Opslaan is mislukt. Controleer je verbinding en probeer opnieuw.', 'Saving didn’t work. Check your connection and try again.'),
  closed:       L('Cette enquête est clôturée. Merci à toutes et à tous !', 'Deze bevraging is afgesloten. Iedereen bedankt!', 'This survey is closed. Thanks, everyone!'),
  tabForm:      L('Mes réponses', 'Mijn antwoorden', 'My answers'),
  tabResults:   L('Résultats', 'Resultaten', 'Results'),
  // Results
  responses:    L('réponses', 'antwoorden', 'responses'),
  ofTeam:       L('de l’équipe', 'van het team', 'of the team'),
  weekly:       L('utilisent l’IA chaque semaine', 'gebruiken AI wekelijks', 'use AI weekly or more'),
  never:        L('ne l’utilisent jamais', 'gebruiken het nooit', 'never use it'),
  wantTraining: L('veulent une formation', 'willen een opleiding', 'want training'),
  demos:        L('prêt·es à faire une démo', 'willen een demo geven', 'would give a demo'),
  allTeams:     L('Toutes les équipes', 'Alle teams', 'All teams'),
  allFreq:      L('Toutes les fréquences', 'Elke frequentie', 'Any usage'),
  levelTitle:   L('Niveau IA', 'AI-niveau', 'AI level'),
  levelNote:    L('Un regroupement approximatif pour organiser les formations, calculé à partir des réponses déclarées. Pas une évaluation, et jamais montré à la personne.', 'Een ruwe indeling om opleidingen te plannen, berekend uit zelf opgegeven antwoorden. Geen beoordeling, en nooit zichtbaar voor de persoon zelf.', 'A rough grouping to plan training, computed from self-reported answers. Not an assessment, and never shown to the person.'),
  risk:         L('données sensibles sur un compte non payé par l’entreprise', 'gevoelige data op een account dat het bedrijf niet betaalt', 'sensitive data on an account the company doesn’t pay for'),
  noAnswers:    L('Pas encore de réponses. Partagez le lien avec l’équipe.', 'Nog geen antwoorden. Deel de link met het team.', 'No answers yet. Share the link with the team.'),
  textAnswers:  L('Réponses libres', 'Open antwoorden', 'Written answers'),
  people:       L('Réponses individuelles', 'Individuele antwoorden', 'Individual answers'),
  pending:      L('Pas encore répondu', 'Nog niet geantwoord', 'Not answered yet'),
  copyLink:     L('Copier le lien', 'Link kopiëren', 'Copy link'),
  csv:          L('Exporter en CSV', 'Exporteren als CSV', 'Export CSV'),
  of:           L('sur', 'van', 'of'),
};

/* ── AI literacy level ─────────────────────────────────────────────
 * A rough grouping for training, not a grade: answers are self-reported
 * and nothing is tested. Shown to HR / admins only, never to the person.
 *
 *   frequency (Q freq)         20
 *   knowledge grid (Q know)    30   4 rows × 0–3
 *   how far it goes (Q depth)  30   ask 1 … runs 6
 *   breadth of uses (Q uses)   10   capped at 5
 *   checks the output (Q trust) 10
 *
 * The risk flag stays out of the score: sensitive data pasted into an
 * account the company doesn't pay for is a governance question, not a
 * skill one. */

const FREQ_PTS  = { never: 0, tried: 1, monthly: 2, weekly: 3, daily: 4 };
const KNOW_PTS  = { unknown: 0, heard: 1, tried: 2, regular: 3 };
const DEPTH_PTS = { ask: 1, draft: 2, files: 3, acts: 4, builds: 5, runs: 6 };
const TRUST_PTS = { always: 10, sometimes: 8, often: 5, rarely: 3 };
const SENSITIVE = ['customer', 'supplier', 'figures'];

export const LEVELS = [
  ['starter',      0, L('Débutant', 'Starter', 'Starter')],
  ['explorer',    25, L('Explorateur', 'Verkenner', 'Explorer')],
  ['practitioner',50, L('Praticien', 'Gevorderd', 'Practitioner')],
  ['builder',     75, L('Bâtisseur', 'Bouwer', 'Builder')],
];

export function aiLevel(a) {
  const knowRows = AI_SURVEY.sections.flatMap(s => s.questions).find(q => q.id === 'know').rows;
  const know = knowRows.reduce((sum, [rid]) => sum + (KNOW_PTS[a.know?.[rid]] || 0), 0);
  const uses = (a.uses || []).length + (a.uses_other ? 1 : 0);
  const score = Math.round(
    ((FREQ_PTS[a.freq] || 0) / 4) * 20
    + (know / (knowRows.length * 3)) * 30
    + ((DEPTH_PTS[a.depth] || 0) / 6) * 30
    + (Math.min(uses, 5) / 5) * 10
    + (TRUST_PTS[a.trust] || 0),
  );
  const level = [...LEVELS].reverse().find(([, min]) => score >= min)[0];
  const risk = (a.data || []).some(d => SENSITIVE.includes(d)) && a.account !== 'company';
  return { score, level, risk };
}
